import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { GraduationCap, Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
      // In dev/preview mode, backend returns the reset URL
      if (response.data.dev_mode && response.data.reset_url) {
        setDevResetUrl(response.data.reset_url);
      }
      toast.success('If this email is registered, a reset link has been sent.');
    } catch (error) {
      // Always show success to prevent email enumeration
      setSubmitted(true);
      toast.success('If this email is registered, a reset link has been sent.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-12" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <GraduationCap className="h-10 w-10 text-indigo-700" />
            <span className="font-heading text-2xl font-semibold text-slate-900">
              Gifted Academy Tutors
            </span>
          </Link>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Forgot Password</h1>
          <p className="text-slate-600 mt-2">Reset your account password</p>
        </div>

        <Card className="shadow-card border-stone-100">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-xl">Reset Password</CardTitle>
            <CardDescription>
              Enter your email address to receive a reset link
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    We'll send a password reset link to this email if it's registered.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-full bg-slate-900 hover:bg-slate-800"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            ) : (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-medium text-slate-900 mb-2">Check Your Email</h3>
                <p className="text-sm text-slate-600 mb-4">
                  If your email is registered, you will receive a password reset link shortly.
                </p>
                <p className="text-xs text-slate-500">
                  The link will expire in 1 hour.
                </p>
                
                {/* Dev/Preview mode: Show reset link directly */}
                {devResetUrl && (
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left">
                    <p className="text-xs font-medium text-amber-800 mb-2">
                      Preview Mode - Reset Link:
                    </p>
                    <a 
                      href={devResetUrl}
                      className="text-xs text-indigo-600 hover:text-indigo-800 break-all underline"
                      data-testid="dev-reset-link"
                    >
                      {devResetUrl}
                    </a>
                    <p className="text-xs text-amber-600 mt-2">
                      (This link is only shown because email is not configured)
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
